import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebaseConfig';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/AdminDashboard';
import VendorDashboard from './pages/VendorDashboard';
import { CircularProgress, Box } from '@mui/material';
import ChangePassword from './pages/ChangePassword';

type Role = 'admin' | 'vendor' | null;

function App() {
  const [user, setUser] = useState<User | null>(null);
  // Claims drive which dashboard a signed-in user may see. The server is
  // still the trust boundary (Firestore rules + role checks in callables);
  // this just stops us rendering the wrong portal's chrome.
  const [role, setRole] = useState<Role>(null);
  const [vendorId, setVendorId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const token = await currentUser.getIdTokenResult();
          setRole((token.claims.role as Role) ?? null);
          setVendorId(token.claims.vendorId as string | undefined);
        } catch (err) {
          console.error('Failed to read custom claims:', err);
          setRole(null);
          setVendorId(undefined);
        }
      } else {
        setRole(null);
        setVendorId(undefined);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Where a signed-in user belongs. A user with no usable role goes back to
  // /login, which shows the "no role assigned" message on their next attempt.
  const homeRoute = role === 'admin' ? '/admin/dashboard' :
    role === 'vendor' && vendorId ? '/vendor/dashboard' :
    '/login';

  const guard = (allowed: Role, element: React.ReactElement) => {
    if (!user) return <Navigate to="/login" replace />;
    if (role !== allowed) return <Navigate to={homeRoute} replace />;
    return element;
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={user ? homeRoute : '/login'} replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/admin/*" element={guard('admin', <AdminDashboard />)} />
        <Route
          path="/vendor/*"
          element={guard('vendor', <VendorDashboard vendorId={vendorId} />)}
        />
        <Route
          path="/change-password"
          element={user ? <ChangePassword /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
